import React, { useEffect, useRef, useState } from "react";
import { Check, Search } from "lucide-react";
import { toast } from "sonner";
import { getFxRate, searchInstruments } from "@/lib/api";
import { formatEUR } from "@/lib/format";
import { CLASS_COLOR, CLASS_LABEL, CurrencyToggle, TEAL, TerminalInput, ccyOptions } from "./shared";

// Form "Aggiungi asset": autonomo (gestisce ricerca, selezione, valuta e anteprima FX).
// Alla conferma chiama onConfirm(payload, symbol); il parent gestisce API + cambio vista.
export const AddAssetForm = ({ busy, onConfirm }) => {
  const [assetClass, setAssetClass] = useState("crypto");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState("");
  const [avg, setAvg] = useState("");
  const [avgCcy, setAvgCcy] = useState("EUR");
  const [fxRate, setFxRate] = useState(null);
  const [brokerVal, setBrokerVal] = useState("");
  const [manualVal, setManualVal] = useState("");
  const debounceRef = useRef(null);

  // ricerca strumenti (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchInstruments(query.trim(), assetClass);
        setResults(res || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, assetClass]);

  // Quando si seleziona uno strumento, imposta la valuta del prezzo di carico
  // sulla valuta nativa dello strumento (es. USD per i titoli americani).
  useEffect(() => {
    if (selected) setAvgCcy((selected.currency || "EUR").toUpperCase());
  }, [selected]);

  // Cambio valuta -> EUR per l'anteprima della conversione.
  useEffect(() => {
    let alive = true;
    if (avgCcy && avgCcy !== "EUR") {
      getFxRate(avgCcy)
        .then((r) => alive && setFxRate(r.rate))
        .catch(() => alive && setFxRate(null));
    } else {
      setFxRate(null);
    }
    return () => {
      alive = false;
    };
  }, [avgCcy]);

  const confirmAdd = async () => {
    if (!selected) return toast.error("Seleziona un asset dalla ricerca");
    const q = parseFloat(String(qty).replace(",", "."));
    const a = parseFloat(String(avg).replace(",", "."));
    if (!q || q <= 0) return toast.error("Inserisci una quantità valida");
    if (Number.isNaN(a) || a < 0) return toast.error("Inserisci un prezzo medio valido");
    const manual = manualVal.trim() ? parseFloat(String(manualVal).replace(",", ".")) : null;
    if (manual !== null && (Number.isNaN(manual) || manual < 0)) return toast.error("Prezzo manuale non valido");
    await onConfirm(
      {
        asset_class: selected.asset_class,
        symbol: selected.symbol,
        name: selected.name || selected.symbol,
        provider_id: selected.provider_id,
        quantity: q,
        avg_price: a,
        avg_price_currency: avgCcy,
        broker: brokerVal.trim() || null,
        manual_price: manual,
      },
      selected.symbol,
    );
  };

  return (
    <div className="space-y-4" data-testid="add-asset-form">
      <div className="flex gap-2">
        {["crypto", "stock"].map((cls) => (
          <button
            key={cls}
            onClick={() => {
              setAssetClass(cls);
              setSelected(null);
              setResults([]);
              setQuery("");
            }}
            data-testid={`add-asset-class-${cls}`}
            className="mono flex-1 rounded-[8px] border py-2.5 text-[11px] font-medium uppercase tracking-[0.14em] transition-colors"
            style={
              assetClass === cls
                ? { borderColor: `${CLASS_COLOR[cls]}66`, color: CLASS_COLOR[cls], background: `${CLASS_COLOR[cls]}12` }
                : { borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }
            }
          >
            {CLASS_LABEL[cls]}
          </button>
        ))}
      </div>

      <div>
        <label className="caps-tag mb-1.5 block text-white/40">Cerca {assetClass === "crypto" ? "criptovaluta" : "titolo / ETF"}</label>
        <div className="flex h-11 items-center gap-2 rounded-[8px] border border-white/10 bg-[#050508] px-3 focus-within:border-[#2DD4BF]/50">
          <Search size={14} className="text-white/35" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(null);
            }}
            placeholder={assetClass === "crypto" ? "es. Bitcoin, Ethereum…" : "es. Apple, VWCE…"}
            data-testid="add-search-input"
            className="mono h-full w-full bg-transparent text-sm font-light text-white placeholder:text-white/25 focus:outline-none"
          />
        </div>
      </div>

      {!selected && (query.trim().length >= 2) && (
        <div className="space-y-1.5 rounded-[8px] border border-white/[0.06] bg-[#0D0D14] p-1.5" data-testid="add-search-results">
          {searching ? (
            <div className="px-2 py-3 text-center text-[12px] font-light text-white/35">Ricerca…</div>
          ) : results.length === 0 ? (
            <div className="px-2 py-3 text-center text-[12px] font-light text-white/35">Nessun risultato</div>
          ) : (
            results.map((r, i) => (
              <button
                key={`${r.provider_id}-${i}`}
                onClick={() => setSelected(r)}
                data-testid={`add-result-${i}`}
                className="flex w-full items-center justify-between rounded-[6px] px-2.5 py-2 text-left hover:bg-white/[0.05]"
              >
                <span className="min-w-0">
                  <span className="mono text-[13px] font-medium text-white/90">{r.symbol}</span>
                  <span className="ml-2 truncate text-[12px] font-light text-white/45">{r.name}</span>
                </span>
                {r.exchange && <span className="mono shrink-0 text-[10px] text-white/30">{r.exchange} · {r.currency}</span>}
              </button>
            ))
          )}
        </div>
      )}

      {selected && (
        <div className="rounded-[10px] border bg-[#0D0D14] p-3" style={{ borderColor: `${CLASS_COLOR[selected.asset_class]}44` }} data-testid="add-selected">
          <div className="flex items-center justify-between">
            <div>
              <p className="mono text-sm font-medium text-white/90">{selected.symbol}</p>
              <p className="text-[12px] font-light text-white/45">{selected.name}</p>
            </div>
            <button onClick={() => setSelected(null)} className="text-[11px] text-white/40 hover:text-white/70" data-testid="add-clear-selected">
              Cambia
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <TerminalInput label="Quantità" value={qty} onChange={setQty} placeholder="0" testid="add-quantity-input" />
            <TerminalInput
              label="Prezzo medio"
              value={avg}
              onChange={setAvg}
              placeholder="0"
              suffix={<CurrencyToggle value={avgCcy} onChange={setAvgCcy} options={ccyOptions(selected.currency)} />}
              testid="add-avgprice-input"
              hint={
                avgCcy !== "EUR" && fxRate && avg
                  ? `≈ ${formatEUR((parseFloat(String(avg).replace(",", ".")) || 0) * fxRate)} · cambio ${avgCcy}→EUR ${fxRate}`
                  : "Prezzo di carico del broker (scegli la valuta)"
              }
            />
          </div>
          <div className="mt-3 flex gap-2">
            <TerminalInput
              label="Broker / Conto"
              type="text"
              value={brokerVal}
              onChange={setBrokerVal}
              placeholder="es. Fineco"
              testid="add-broker-input"
            />
            <TerminalInput
              label="Prezzo manuale"
              value={manualVal}
              onChange={setManualVal}
              placeholder="opzionale"
              suffix="€"
              testid="add-manualprice-input"
              hint="Se il prezzo live non è disponibile"
            />
          </div>
          <button
            onClick={confirmAdd}
            disabled={busy}
            data-testid="add-confirm-button"
            className="mono mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-[6px] border bg-transparent text-[11px] font-medium uppercase tracking-[0.18em] active:scale-[0.98] disabled:opacity-50"
            style={{ borderColor: `${TEAL}55`, color: TEAL, boxShadow: `0 0 20px ${TEAL}14` }}
          >
            <Check size={14} />
            {busy ? "Aggiungo…" : "Aggiungi al portafoglio"}
          </button>
        </div>
      )}
    </div>
  );
};
